import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import PersonalizedHero from './components/PersonalizedHero';
import FeaturedProperties from './components/FeaturedProperties';
import Footer from './components/Footer';
import WelcomeOnboarding, { STORAGE_KEY } from './components/WelcomeOnboarding';
import PWAInstallBadge from '../../components/PWAInstallBadge';
import PWAWalkthrough from '../../components/PWAWalkthrough';
import { PWA_WALKTHROUGH_KEY } from '../../components/PWAWalkthrough';
import { useAuth } from '../../contexts/AuthContext';

const Homepage = () => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showPWAWalkthrough, setShowPWAWalkthrough] = useState(false);
  const { user, loading } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
    // Afficher l'onboarding uniquement pour les nouveaux visiteurs non connectés
    if (!user) {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) {
        const timer = setTimeout(() => setShowOnboarding(true), 600);
        return () => clearTimeout(timer);
      }
    }
  }, [user]);

  // Gérer l'affichage du guide PWA
  useEffect(() => {
    const walkthroughSeen = localStorage.getItem(PWA_WALKTHROUGH_KEY);
    if (walkthroughSeen) return;

    const isStandalone =
      window.matchMedia('(display-mode: standalone)')?.matches ||
      window.navigator?.standalone === true;

    if (isStandalone) {
      const t = setTimeout(() => setShowPWAWalkthrough(true), 1200);
      return () => clearTimeout(t);
    }

    const handleInstalled = () => {
      const alreadySeen = localStorage.getItem(PWA_WALKTHROUGH_KEY);
      if (!alreadySeen) {
        setTimeout(() => setShowPWAWalkthrough(true), 1500);
      }
    };
    window.addEventListener('appinstalled', handleInstalled);
    return () => window.removeEventListener('appinstalled', handleInstalled);
  }, []);

  return (
    <>
      <Helmet>
        <title>Kapital States - Plateforme Immobilière Premium</title>
        <meta 
          name="description" 
          content="Découvrez l'immobilier d'exception en Côte d'Ivoire. Visites immersives, sécurité juridique intégrée et transactions garanties." 
        />
        <meta name="keywords" content="immobilier, abidjan, propriétés, investissement, visites virtuelles, premium" />
        <meta property="og:title" content="Kapital States - Plateforme Immobilière Premium" />
        <meta property="og:description" content="L'écosystème immobilier de confiance." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://kapitalstates.com" />
        <link rel="canonical" href="https://kapitalstates.com" />
      </Helmet>

      {/* 
        Le conteneur principal: 
        Fond noir profond (#050505) qui se fond avec le Header et le Footer.
        AUCUNE bordure (border-0, border-none, outline-none).
      */}
      <div className="min-h-screen bg-[#050505] text-white flex flex-col relative overflow-x-hidden border-none outline-none">
        
        {/* 
          Couche d'ambiance lumineuse (Glows) :
          Ici, on garde vos orbes (qui créent l'effet premium d'Apple).
          Ils doivent être subtils et ne créer aucune ligne dure.
        */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="homepage-mesh-base" />
          <div className="homepage-orb homepage-orb-green-tl opacity-30" />
          <div className="homepage-orb homepage-orb-blue-tr opacity-30" />
          <div className="homepage-orb homepage-orb-green-ml opacity-20" />
          <div className="homepage-orb homepage-orb-blue-br opacity-20" />
          <div className="homepage-orb homepage-orb-center opacity-10" />
          <div className="homepage-streak homepage-streak-1 opacity-40" />
          <div className="homepage-streak homepage-streak-2 opacity-40" />
        </div>

        {/* 
          Le Header :
          On s'assure qu'il n'a ni bordure ni ombre dure qui viendrait 
          casser la fluidité avec la section en dessous.
        */}
        <div className="relative z-40 border-none shadow-none">
          <Header className="pl-[21px] pr-[15px] pt-3 border-none bg-transparent" />
        </div>

        {/* 
          Le contenu principal :
          S'étend pour pousser le footer vers le bas, sans bordure.
        */}
        <main className="relative z-10 flex-grow flex flex-col w-full border-none">
          
          {/* Section Héro : La transition avec le fond doit être invisible */}
          <div className="w-full border-none outline-none pb-12">
            {!loading && user ? <PersonalizedHero /> : <HeroSection />}
          </div>

          {/* 
            Propriétés à la une : 
            J'ai supprimé votre classe "homepage-section-glass" ici 
            car souvent le glassmorphism crée une bordure blanche autour. 
            On laisse le fond se fondre naturellement.
          */}
          <div className="w-full border-none outline-none relative z-20 pb-20">
            <FeaturedProperties />
          </div>
          
        </main>

        {/* Le Footer que nous venons de designer, il se posera sans ligne de démarcation */}
        <div className="relative z-30 border-none w-full mt-auto">
          <Footer />
        </div>
      </div>

      {/* Overlays / Popups */}
      {showOnboarding && (
        <WelcomeOnboarding onDismiss={() => setShowOnboarding(false)} />
      )}
      {showPWAWalkthrough && (
        <PWAWalkthrough onDismiss={() => setShowPWAWalkthrough(false)} />
      )}
      <PWAInstallBadge />
    </>
  );
};

export default Homepage;